import { useEffect, useCallback, useRef } from 'react';
import { parseSRT, parseVTT } from '../utils/captionParser';
import type { PlayerAction } from '../playerReducer';
import type { TrackInfo, CaptionCue } from '../types';

export function useCaptions(
  mediaEl: HTMLMediaElement | null,
  dispatch: React.Dispatch<PlayerAction>,
  selectedTrackIdx: number,
  captions: CaptionCue[],
  currentTime: number,
) {
  const lastCueTextRef = useRef('');
  // Discover tracks from the video element
  useEffect(() => {
    if (!mediaEl) return;

    const discoverTracks = () => {
      const tracks: TrackInfo[] = [];
      const trackEls = mediaEl.querySelectorAll('track');
      trackEls.forEach((t) => {
        tracks.push({
          label: t.label || t.srclang || 'Unknown',
          srclang: t.srclang || '',
          src: t.src,
          kind: t.kind || 'subtitles',
        });
        // Disable native rendering so we can render our own overlay
        t.track.mode = 'disabled';
      });
      if (tracks.length > 0) {
        dispatch({ type: 'SET_CAPTION_TRACKS', tracks });
      }
    };

    discoverTracks();

    // Also listen for dynamically added tracks
    const observer = new MutationObserver(discoverTracks);
    observer.observe(mediaEl, { childList: true });

    return () => observer.disconnect();
  }, [mediaEl, dispatch]);

  // Load and parse the selected track
  const loadTrack = useCallback(
    async (track: TrackInfo) => {
      try {
        const response = await fetch(track.src);
        const text = await response.text();

        let cues: CaptionCue[];
        if (track.src.endsWith('.srt')) {
          cues = parseSRT(text);
        } else {
          cues = parseVTT(text);
        }

        dispatch({ type: 'SET_CAPTIONS', captions: cues });
      } catch {
        dispatch({ type: 'SET_CAPTIONS', captions: [] });
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (selectedTrackIdx < 0) {
      dispatch({ type: 'SET_CAPTIONS', captions: [] });
      dispatch({ type: 'SET_ACTIVE_CUE', text: '' });
      lastCueTextRef.current = '';
      return;
    }
  }, [selectedTrackIdx, dispatch]);

  // Update active cue based on currentTime — only dispatch when
  // the text actually changes to avoid unnecessary re-renders
  useEffect(() => {
    if (captions.length === 0) {
      return;
    }

    const activeCue = captions.find(
      (cue) => currentTime >= cue.start && currentTime <= cue.end,
    );

    const text = activeCue ? activeCue.content : '';
    if (text !== lastCueTextRef.current) {
      lastCueTextRef.current = text;
      dispatch({ type: 'SET_ACTIVE_CUE', text, end: activeCue?.end ?? 0 });
    }
  }, [currentTime, captions, dispatch]);

  return { loadTrack };
}
